import AddToCalendar from '@liveauctioneers/caterwaul-components/lib/AddToCalendar/AddToCalendar';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Countdown } from '@liveauctioneers/hammer-ui-core/countdown';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import {
    getCatalogDescription,
    getCatalogIsCatalogOnly,
    getCatalogIsInstantAutopay,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogStatus,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getIsRegistered } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { useTrackAddToCalendarPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackAddToCalendarPressed.analytics';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { calendarEvent } from '@/utils/calendarEvent';
import { shouldShowLive } from '@/utils/live';

import { CatalogRegistrationMessages } from './CatalogRegistrationMessages';

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive: boolean;
    setForceIsLive: (force: boolean) => void;
};

const TimedOrLiveAuctionStatus = ({ catalogId, className, forceIsLive, setForceIsLive }: Props) => {
    const { variants } = useAmplitudeExperiments();
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const featureFlags = useFeatureFlags();

    const reloadPageData = useCallback(
        () => dispatch(loadPageData({ amplitudeExperimentFlags: variants, featureFlags, location })),
        [dispatch, location, variants, featureFlags]
    );
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const isLive = forceIsLive ? true : shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const event = calendarEvent({
        catalogId,
        description,
        saleStartTs,
        sellerName,
        title,
    });

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();
    const trackAddToCalendarPressedAnalytics = useTrackAddToCalendarPressedAnalytics();

    const registerForCatalog = useCallback(() => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    }, [catalogId, dispatch, isInstantAutopay, modalContext, trackRegisterForAuctionPressedAnalytics]);

    const trackAddToCalendar = (_calendarTypeId: string, calendarType: string) => {
        trackAddToCalendarPressedAnalytics({ calendarType });
    };

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start', className)}
            data-testid="timed-or-live-auction-status"
        >
            {registered && <CatalogRegistrationMessages catalogId={catalogId} />}
            {!isLive ? (
                <div suppressHydrationWarning>
                    {/* Match `CatalogCurators` `StyledLabel`: span, 14px / 20px lh, 1px tracking, grey100 */}
                    <span className="mb-[18px] block w-full text-center font-primary text-sm uppercase leading-5 tracking-[1px] text-[color:var(--grey100)]">
                        <FormattedMessage id="catalogPage.catalogStatus.biddingStarts" />
                    </span>
                    <div
                        className="mb-lg mt-sm flex justify-center [&_.hui-text-body-secondary]:hui-text-h5"
                        data-testid="countdown"
                    >
                        <Countdown
                            completionTime={saleStartTs * 1000}
                            forceAllUnits
                            onComplete={() => {
                                setForceIsLive(true);
                                reloadPageData();
                            }}
                            small
                            timeLabels={{ days: ' days', hours: ' hrs', minutes: ' mins', seconds: ' secs' }}
                        />
                    </div>
                </div>
            ) : (
                <BidConsoleOpener
                    catalogId={catalogId}
                    shouldTrackEnterLiveAuction
                >
                    <Button
                        className="mb-sm w-full xsMax:mb-xs xsMax:mt-sm"
                        data-testid="catalogEnterLiveAuctionButton"
                        style={isWhiteLabel ? 'primary' : 'critical'}
                        variant="filled"
                    >
                        {formatMessage({ id: 'enter_live_auction' })}
                    </Button>
                </BidConsoleOpener>
            )}
            {!registered && (
                <Button
                    className="mb-sm w-full"
                    data-testid="liveCatalogRegisterButton"
                    onClick={registerForCatalog}
                    style={isLive ? 'critical' : 'primary'}
                    variant={isLive ? 'outlined' : 'filled'}
                >
                    {formatMessage({ id: 'register_for_auction' })}
                </Button>
            )}
            {!isLive && (
                <AddToCalendar
                    buttonLabel={formatMessage({ id: 'add_to_calendar' })}
                    buttonWrapperClass="w-full [&>button]:!block [&>button]:!w-full"
                    className={cn('!relative !mb-0 !block !w-full max-w-full')}
                    event={event}
                    onClick={trackAddToCalendar}
                />
            )}
            {!registered && isLive && (
                <BodySecondary
                    className="block text-center"
                    color="secondary"
                >
                    <FormattedMessage id="unregistered_message" />
                </BodySecondary>
            )}
        </div>
    );
};

export default TimedOrLiveAuctionStatus;
